import {
  Box,
  Button,
  ChevronLeftIcon,
  DismissIcon,
  Modal,
  Typography,
  useDrawerContext,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { createContext, useContext, useId } from 'react';

import { BottomSheet } from '../../../bottomSheet/bottomSheet';

import styles from './rateAndReviewModal.module.css';

export interface RateAndReviewModalProps {
  /**
   * A body for the current phase of the flow (form, stepper, submitted).
   * Bodies render their own `RateAndReviewModal.Body` and
   * `RateAndReviewModal.Footer`, so each variant owns its actions.
   */
  children: ReactNode;
  /** Applied to the desktop dialog only. */
  className?: string;
  /** Bottom sheet on mobile, centered dialog otherwise. */
  isMobile: boolean;
  /** Renders a back button left of the title when provided. */
  onBack?: () => void;
  onRequestClose: () => void;
  open: boolean;
  /** Rendered below the title, stays pinned while the body scrolls. */
  productHeader?: ReactNode;
  testId?: string;
  /** Defaults to "Rate and review". */
  title?: string;
}

const SHEET_HEIGHT = '90dvh';

interface SlotProps {
  children: ReactNode;
  testId?: string;
}

/**
 * Tells the slots which shell they're in. Reefer's `Modal.Content` and
 * `Modal.Footer` size themselves against a `Modal`, so the sheet uses its own.
 */
const ShellContext = createContext({ isMobile: false });
const SHEET_SHELL = { isMobile: true };

/** Scrolling content area between sticky header and footer. */
function RateAndReviewModalBody({
  children,
  testId = 'rate-and-review-modal-body',
}: SlotProps) {
  const { isMobile } = useContext(ShellContext);

  if (isMobile) {
    return (
      <div className={styles['sheetBody']} data-testid={testId}>
        {children}
      </div>
    );
  }

  return <Modal.Content data-testid={testId}>{children}</Modal.Content>;
}

/** Sticky footer - action bar */
function RateAndReviewModalFooter({
  children,
  testId = 'rate-and-review-modal-footer',
}: SlotProps) {
  const { isMobile } = useContext(ShellContext);

  if (isMobile) {
    return (
      <div
        className={clsx(styles['footer'], styles['sheetFooter'])}
        data-testid={testId}
      >
        {children}
      </div>
    );
  }

  return (
    <Modal.Footer className={styles['footer']} data-testid={testId}>
      {children}
    </Modal.Footer>
  );
}

interface CloseButtonProps {
  isMobile: boolean;
  onRequestClose: () => void;
}

/** On mobile, closes through the sheet so it slides out first. */
function CloseButton({ isMobile, onRequestClose }: CloseButtonProps) {
  const { onRequestClose: closeSheet } = useDrawerContext();

  return (
    <Button.Icon
      className={styles['close']}
      icon={<DismissIcon />}
      label="Close"
      onClick={isMobile ? closeSheet : onRequestClose}
    />
  );
}

/**
 * Modal shell for the Rate & Review flow.
 * Owns the frame (title, close, product header); the body passed as children
 * owns its content and actions.
 */
export function RateAndReviewModal({
  children,
  className,
  isMobile,
  onBack,
  onRequestClose,
  open,
  productHeader,
  testId = 'rate-and-review-modal',
  title = 'Rate and review',
}: RateAndReviewModalProps) {
  const titleId = useId();

  const frame = (
    <>
      <div className={styles['header']} data-testid={`${testId}-header`}>
        <div className={styles['titleRow']}>
          {onBack && (
            <Button.Icon
              className={styles['back']}
              icon={<ChevronLeftIcon />}
              label="Back"
              onClick={onBack}
            />
          )}
          <Typography
            as="h1"
            branded
            className={styles['title']}
            id={titleId}
            textAlign="center"
            variant="h2-semibold"
          >
            {title}
          </Typography>
          <CloseButton isMobile={isMobile} onRequestClose={onRequestClose} />
        </div>
        {productHeader && (
          <Box pt={16} width="100%">
            {productHeader}
          </Box>
        )}
      </div>
      {children}
    </>
  );

  if (isMobile) {
    // Fixed height so the sheet doesn't jump between phases and wizard steps.
    // Overlay taps don't close it, so a stray tap can't discard a review.
    return (
      <BottomSheet
        aria-labelledby={titleId}
        data-testid={testId}
        height={SHEET_HEIGHT}
        onRequestClose={onRequestClose}
        open={open}
        overlayClose={false}
      >
        <ShellContext.Provider value={SHEET_SHELL}>
          <div className={styles['sheet']}>{frame}</div>
        </ShellContext.Provider>
      </BottomSheet>
    );
  }

  return (
    <Modal
      background="grays-white"
      className={clsx(styles['dialog'], className)}
      contentLabel={title}
      data-testid={testId}
      onRequestClose={onRequestClose}
      open={open}
      variant="standard-dialogue"
    >
      {frame}
    </Modal>
  );
}

RateAndReviewModal.Body = RateAndReviewModalBody;
RateAndReviewModal.Footer = RateAndReviewModalFooter;
